import { create } from 'zustand';
import { useMeetingStore } from './meeting.store';
import { useLicenseFloorUtils } from '../hooks/useLicenseFloorUtils';
import {
  clearRecordingDurationWatcher,
  startRecordingDurationWatcher,
} from './utils/recording-utils';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import { useWorkplaceStore } from './workplace.store';

export type RecordingStatusEnum = 'Completed' | 'In Progress' | 'Failed';
export const RECORDING_MAX_DURATION_MS = 2 * 60 * 60 * 1000;

export interface Recording {
  id: string;
  title: string;
  status: RecordingStatusEnum | string;
  createdDate: string;
  recordedBy: string;
  recordedById: string;
  duration?: string;
  url?: string;
  notes?: string;
  transcription?: string;
  isDeleted?: boolean;
  type?: 'recording' | 'standalone_transcription';
}

interface Filter {
  status?: { label: string; value: string } | null;
  recordedBy?: { label: string; value: string } | null;
  room?: { label: string; value: string } | null;
  date?: { startDate: Date | null; endDate: Date | null } | null;
}

interface RecordingStore {
  filters: Filter | null;
  recordings: any[];
  myRecordings: any[];
  recordingsSharedWithMe: any[];
  recycleBinRecordings: any[];
  selectedRecording: any | null;

  roomRecordingStatus: string;
  activeRecordingSession: any;
  activeRecordingInfo: any;
  roomRecordingData: any;

  // Manual transcription state (independent of recording)
  isManualTranscriptionActive: boolean;
  manualTranscriptionInitiatorPid: string | null;
  manualTranscriptionStartTime: Date | null;

  temporaryModeratorId?: string | null;
  recordingStopTimeoutId?: ReturnType<typeof setTimeout> | null;
  recordingDurationWatcherId?: ReturnType<typeof setTimeout> | null;
  transcriptionStopTimeoutId?: ReturnType<typeof setTimeout> | null;

  setTemporaryModerator: (id: string | null) => void;
  setRecordingStopTimeoutId: (id: ReturnType<typeof setTimeout> | null) => void;
  setRecordingDurationWatcherId: (id: ReturnType<typeof setTimeout> | null) => void;
  setTranscriptionStopTimeoutId: (id: ReturnType<typeof setTimeout> | null) => void;

  isAiSummaryGenerating: boolean;
  setIsAiSummaryGenerating: (value: boolean) => void;

  showAiNoTranscriptionBanner: boolean;
  setShowAiNoTranscriptionBanner: (value: boolean) => void;

  // Manual transcription actions
  setManualTranscriptionActive: (active: boolean, initiatorPid?: string) => void;

  setActiveRecordingInfo: (data: any) => void;
  setActiveRecordingSession: (data: any) => void;
  setRecordings: (recordings: Recording[]) => void;
  selectRecording: (recording: Recording | null) => void;
  clearSelection: () => void;
  setFilters: (filters: Filter | null) => void;
  setMyRecordings: (recordings: any[]) => void;
  setRecordingsSharedWithMe: (recordings: any[]) => void;
  setRecycleBinRecordings: (recordings: any[]) => void;
  setRoomRecordingStatus: (value: string) => void;
  clearRecordingSession: () => void;
}

export const useRecordingStore = create<RecordingStore>()(
  subscribeWithSelector((set, get) => ({
    recordings: [],
    myRecordings: [],
    recordingsSharedWithMe: [],
    recycleBinRecordings: [],
    selectedRecording: null,
    filters: {
      status: null,
      recordedBy: null,
      room: null,
    },
    roomRecordingData: null,
    roomRecordingStatus: 'off',
    activeRecordingSession: null,
    activeRecordingInfo: null,
    recordingDurationWatcherId: null,

    isAiSummaryGenerating: false,
    showAiNoTranscriptionBanner: false,

    // Manual transcription state
    isManualTranscriptionActive: false,
    manualTranscriptionInitiatorPid: null,
    manualTranscriptionStartTime: null,

    temporaryModeratorId: null,
    recordingStopTimeoutId: null,
    transcriptionStopTimeoutId: null,

    setActiveRecordingInfo: (data) => {
      set(() => ({ activeRecordingInfo: data }));
      if (data && get().roomRecordingStatus === 'on') {
        startRecordingDurationWatcher();
      } else {
        clearRecordingDurationWatcher();
      }
    },
    setActiveRecordingSession: (data) =>
      set(() => ({ activeRecordingSession: data, roomRecordingStatus: data._statusFromJicofo })),
    setIsAiSummaryGenerating: (value) => set({ isAiSummaryGenerating: value }),
    setShowAiNoTranscriptionBanner: (value) => set({ showAiNoTranscriptionBanner: value }),
    setRoomRecordingStatus: (status: string) => set(() => ({ roomRecordingStatus: status })),
    setRecordings: (recordings: Recording[]) => set({ recordings: recordings }),
    setMyRecordings: (recordings: any[]) => set({ myRecordings: recordings }),
    setRecordingsSharedWithMe: (recordings: any[]) => set({ recordingsSharedWithMe: recordings }),
    setRecycleBinRecordings: (recordings: any[]) => set({ recycleBinRecordings: recordings }),
    selectRecording: (recording: Recording | null) => set({ selectedRecording: recording }),
    clearSelection: () => set({ selectedRecording: null }),
    setFilters: (filters: Filter | null) => set({ filters: filters }),
    setTemporaryModerator: (id) => set({ temporaryModeratorId: id }),
    setRecordingStopTimeoutId: (id) => set({ recordingStopTimeoutId: id }),
    setRecordingDurationWatcherId: (id) => set({ recordingDurationWatcherId: id }),
    setTranscriptionStopTimeoutId: (id) => set({ transcriptionStopTimeoutId: id }),

    // Manual transcription actions
    setManualTranscriptionActive: (active, initiatorPid) => {
      set({
        isManualTranscriptionActive: active,
        manualTranscriptionInitiatorPid: active ? initiatorPid || null : null,
        manualTranscriptionStartTime: active ? new Date() : null,
      });
    },

    clearRecordingSession: () => {
      set(() => ({
        activeRecordingInfo: null,
        activeRecordingSession: null,
        roomRecordingStatus: 'off',

        // Clear manual transcription state
        isManualTranscriptionActive: false,
        manualTranscriptionInitiatorPid: null,
        manualTranscriptionStartTime: null,

        showAiNoTranscriptionBanner: false,
      }));
    },
  }))
);

export const useShouldShowRecordingBar = () => {
  const roomRecordingStatus = useRecordingStore((s) => s.roomRecordingStatus);
  const activeRecordingInfo = useRecordingStore((s) => s.activeRecordingInfo);
  const currentRoomId = useMeetingStore((s) => s.getCurrentRoomPid());
  const { isCurrentUserWebex } = useLicenseFloorUtils();

  const hasRecordingOn = roomRecordingStatus === 'on';
  const isCurrentRoomRecorded = currentRoomId === activeRecordingInfo?.recordingRoomPid;

  if (isCurrentUserWebex()) return false;
  return hasRecordingOn && isCurrentRoomRecorded;
};

/**
 * Checks if the current user is the recording initiator.
 * Lightweight selector with no heavy dependencies (sweetalert2, MeetingService, etc.).
 * @returns {boolean} True if the current user initiated the active recording
 */
export const isCurrentUserInitiator = () => {
  const { activeRecordingInfo } = useRecordingStore.getState();
  const { user } = useWorkplaceStore.getState();
  return activeRecordingInfo?.initiatorUserPid === user?.pid;
};
